import { Dialog, Transition } from '@headlessui/react';
import { CheckCircle } from 'lucide-react';
import { Fragment, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import useUserOrg from '../../Authentication/components/hooks/useUserOrg';
import { useContext } from 'react';
import AuthContext from '../../Authentication/components/AuthContext';
import { Button } from '@/src/shadComponents/ui/button';

interface ReassignmentSuccessModalProps {
    activationId: number;
    activationName: string;
    oldOrgName: string;
    newOrgId: number;
    newOrgName: string;
    onDismiss: () => void;
}

export default function ReassignmentSuccessModal({
    activationId,
    activationName,
    oldOrgName,
    newOrgId,
    newOrgName,
    onDismiss,
}: ReassignmentSuccessModalProps) {
    const cancelButtonRef = useRef(null);
    const navigate = useNavigate();
    const { setOrg } = useUserOrg();
    const { auth } = useContext(AuthContext);

    const handleSwitchOrg = () => {
        if (setOrg && auth?.orgs) {
            // Find the full org object from the user's available orgs
            const targetOrg = auth.orgs.find((org: any) => org.id === newOrgId);
            if (targetOrg) {
                setOrg(targetOrg);
                toast.success(`Switched to ${newOrgName} organization`);
                onDismiss();
                // Optionally refresh the page to update all data
                window.location.reload();
            } else {
                toast.error(
                    'Target organization not found in your available organizations',
                );
            }
        }
    };

    const handleGoToActivationsList = () => {
        navigate('/activations');
        onDismiss();
    };

    return (
        <Transition.Root show={true} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-10"
                initialFocus={cancelButtonRef}
                onClose={() => onDismiss()}
                open={true}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-muted/500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-background text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-background px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[99999px] bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <CheckCircle
                                                className="h-6 w-6 text-green-600"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <h3
                                                className="text-base font-semibold text-foreground"
                                            >
                                                Activation Reassigned
                                                Successfully
                                            </h3>
                                            <div className="mt-2">
                                                <p className="text-sm text-muted-foreground">
                                                    "{activationName}" has been
                                                    successfully reassigned from
                                                    "{oldOrgName}" to "
                                                    {newOrgName}".
                                                </p>
                                                <p className="text-sm text-foreground mt-3 font-medium">
                                                    What would you like to do
                                                    next?
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-muted/50 px-4 py-3 sm:px-6 relative">
                                    <div className="flex flex-col space-y-2">
                                        <Button
                                            type="button"
                                            onClick={handleSwitchOrg}
                                            title={`Switch to ${newOrgName} and stay on this page`}
                                        >
                                            Switch & Stay
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleGoToActivationsList}
                                        >
                                            Go to Activations List
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => onDismiss()}
                                            ref={cancelButtonRef}
                                        >
                                            Close
                                        </Button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
